import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShoppingBag, Minus, Plus } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import AddToCartModal from "@/components/AddToCartModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { resolveProductOptions } from "@/lib/productOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const previewChip = {
  border: "1px solid rgba(255,255,255,0.15)",
  padding: "0.3rem 0.7rem",
  color: "#A1A1AA",
  fontSize: "0.65rem",
  borderRadius: 4,
  fontFamily: "Unbounded, sans-serif",
};

const previewLabel = {
  fontFamily: "Unbounded, sans-serif",
  fontSize: "0.6rem",
  color: "#A1A1AA",
  letterSpacing: "0.15em",
  marginBottom: "0.5rem",
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products/${id}`)
      .then(res => setProduct(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const opts = resolveProductOptions(product);
  const needsModal = product && (opts.showSizes || opts.showColors || opts.showCustom);

  const handleAdd = () => {
    if (!product) return;
    if (needsModal) { setModalOpen(true); return; }
    addItem(product, quantity);
  };

  if (loading) return (
    <div style={{ background: "#050505", minHeight: "100vh" }}>
      <Navbar />
      <div style={{ paddingTop: "8rem", textAlign: "center" }}>
        <p style={{ color: "#52525B", fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem" }}>{t.common.loading}</p>
      </div>
    </div>
  );

  if (!product) return (
    <div style={{ background: "#050505", minHeight: "100vh" }}>
      <Navbar />
      <div style={{ paddingTop: "8rem", textAlign: "center" }}>
        <Link to="/merch" style={{ color: "#FF005A" }}>← {t.common.back}</Link>
      </div>
    </div>
  );

  return (
    <div style={{ background: "#050505", minHeight: "100vh" }}>
      <SEO
        title={`${product.name} – Oficiální merch kapely Rebelky`}
        description={`Kup ${product.name} v oficiálním e-shopu kapely Rebelky. ${product.description?.slice(0, 110) || "Kvalitní fanouškovský merch s rychlým doručením po ČR."} Cena ${product.price} Kč.`}
        url={`/merch/${product._id}`}
        image={product.image_url}
        type="product"
      />
      <Navbar />
      <div style={{ paddingTop: "5rem" }}>
        <div className="max-w-5xl mx-auto px-6 py-12">
          <Link
            to="/merch"
            data-testid="back-to-merch"
            className="flex items-center gap-2 mb-8"
            style={{ color: "#A1A1AA", fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", letterSpacing: "0.15em", textDecoration: "none" }}
          >
            <ArrowLeft size={14} /> {t.common.back}
          </Link>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Image */}
            <div style={{ aspectRatio: "1", background: "#0A0A0A", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
              <img
                src={product.image_url}
                alt={product.name}
                loading="eager"
                decoding="async"
                fetchpriority="high"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={e => { e.target.src = "https://images.unsplash.com/photo-1603208636533-5b965ef2dc22?w=600&q=80"; }}
              />
            </div>

            {/* Details */}
            <div>
              <p className="section-label mb-3">{product.category}</p>
              <h1 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "clamp(1.2rem, 3vw, 1.8rem)", color: "#FFFFFF", textTransform: "uppercase", lineHeight: 1.2, marginBottom: "1rem" }}>
                {product.name}
              </h1>
              <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "2.5rem", color: "#FF005A", marginBottom: "1.5rem" }}>
                {product.price} Kč
              </p>

              {/* Stock badge */}
              {(product.stock ?? 0) <= 0 ? (
                <div data-testid="stock-out" style={{ marginBottom: "1.2rem", padding: "0.7rem 1rem", background: "rgba(229,255,0,0.08)", border: "1px solid rgba(229,255,0,0.35)", borderRadius: 6, color: "#E5FF00", fontFamily: "Manrope, sans-serif", fontSize: "0.8rem", lineHeight: 1.5 }}>
                  <strong style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.65rem", letterSpacing: "0.15em" }}>★ NENÍ SKLADEM</strong>
                  <br />
                  {lang === "cz"
                    ? "Tento produkt momentálně není skladem – po objednání ho vyrobíme a zašleme do 2 týdnů."
                    : "Currently out of stock — we'll make it for you and ship within 2 weeks after order."}
                </div>
              ) : (product.stock ?? 99) < 5 ? (
                <p data-testid="stock-low" style={{ color: "#E5FF00", fontFamily: "Unbounded, sans-serif", fontSize: "0.65rem", letterSpacing: "0.15em", marginBottom: "1rem" }}>
                  ★ Zbývá pouze {product.stock} ks
                </p>
              ) : null}

              {product.description && (
                <p style={{ color: "#A1A1AA", fontSize: "0.9rem", lineHeight: 1.7, marginBottom: "1.5rem" }}>
                  {product.description}
                </p>
              )}

              {/* Options preview — same resolveProductOptions as AddToCartModal */}
              {opts.showSizes && (
                <div className="mb-4" data-testid="pdp-sizes-preview">
                  <p style={previewLabel}>
                    {lang === "cz" ? "DOSTUPNÉ VELIKOSTI" : "AVAILABLE SIZES"}
                  </p>
                  {opts.hasAdultChildToggle ? (
                    <div className="flex flex-col gap-3">
                      <div>
                        <p style={{ ...previewLabel, color: "#52525B", marginBottom: "0.35rem" }}>
                          {lang === "cz" ? "DOSPĚLÝ" : "ADULT"}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {opts.adultSizes.map((s) => (
                            <span key={`a-${s}`} style={previewChip}>{s}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p style={{ ...previewLabel, color: "#52525B", marginBottom: "0.35rem" }}>
                          {lang === "cz" ? "DĚTSKÝ VĚK" : "CHILD AGE"}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {opts.childSizes.map((s) => (
                            <span key={`c-${s}`} style={previewChip}>{s}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 flex-wrap">
                      {opts.allSizeLabels.map((s) => (
                        <span key={s} style={previewChip}>{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {opts.showColors && (
                <div className="mb-4" data-testid="pdp-colors-preview">
                  <p style={previewLabel}>
                    {lang === "cz" ? "BARVY" : "COLORS"}
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {opts.colors.map((c) => (
                      <span key={c.name} title={c.name} style={{ width: 26, height: 26, borderRadius: "50%", background: c.hex, border: "2px solid rgba(255,255,255,0.15)" }} />
                    ))}
                  </div>
                </div>
              )}

              {opts.showCustom && (
                <div className="mb-4" data-testid="pdp-custom-preview">
                  {opts.customVariants.map((cv) => (
                    <div key={cv.label} className="mb-3">
                      <p style={previewLabel}>{cv.label.toUpperCase()}</p>
                      <div className="flex gap-2 flex-wrap">
                        {cv.options.map((o) => (
                          <span key={`${cv.label}-${o}`} style={previewChip}>{o}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Quantity */}
              <div className="mb-6">
                <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", color: "#A1A1AA", letterSpacing: "0.15em", marginBottom: "0.5rem" }}>
                  {t.merch.quantity.toUpperCase()}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    data-testid="qty-decrease"
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "white", width: "36px", height: "36px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <Minus size={14} />
                  </button>
                  <span data-testid="qty-value" style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "1rem", color: "#FFFFFF", minWidth: "2rem", textAlign: "center" }}>
                    {quantity}
                  </span>
                  <button
                    data-testid="qty-increase"
                    onClick={() => setQuantity(q => q + 1)}
                    style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "white", width: "36px", height: "36px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              <button
                data-testid="add-to-cart-btn"
                onClick={handleAdd}
                className="btn-rebel rounded-none w-full flex items-center justify-center gap-2"
                style={{ padding: "1rem", background: "#FF005A" }}
              >
                <ShoppingBag size={16} />
                {needsModal ? (lang === "cz" ? "VYBRAT VELIKOST" : "PICK SIZE") : t.merch.addToCart}
              </button>
            </div>
          </div>
        </div>
      </div>
      <AddToCartModal
        product={product}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={({ size, color, custom_options, quantity: q }) => addItem(product, q || quantity, size, color, custom_options)}
      />
      <Footer />
    </div>
  );
}
